import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetNotificationQueryKey,
  getGetNotificationUnreadCountQueryKey,
  useGetNotification,
  useGetNotificationUnreadCount,
  usePutNotificationIdRead,
  usePutNotificationReadAll,
} from '../api/notification-api/notification-api';
import type { ObjectTarget } from '../components/ReportMetrics/detail';
import { NotificationDto, NotificationEntityType } from '../api/model';

const POLL_INTERVAL_MS = 30_000;

export const INBOX_LIMIT = 50;

const INBOX_KEY = getGetNotificationQueryKey();
const UNREAD_KEY = getGetNotificationUnreadCountQueryKey();

interface UseNotificationInboxOptions {
  enabled?: boolean;
  isOpen?: boolean;
}

// Where clicking a notification should land: the object's own detail, opened
// over the project's current page, or - without an object - a page of the project.
export interface NotificationTarget {
  projectId: number;
  section?: string;
  object?: ObjectTarget;
}

export const notificationTarget = (notification: NotificationDto): NotificationTarget | null => {
  const { projectId, entityType, entityId } = notification;
  if (!projectId) return null;

  const withObject = (kind: ObjectTarget['kind'], section: string): NotificationTarget =>
    entityId ? { projectId, object: { kind, id: entityId } } : { projectId, section };

  switch (entityType) {
    case NotificationEntityType.TASK:
      return withObject('task', 'tasks');
    case NotificationEntityType.ACTION_POINT:
      return withObject('actionPoint', 'task-action-points');
    case NotificationEntityType.CHECKPOINT:
      return withObject('checkpoint', 'check-plan');
    case NotificationEntityType.DELIVERABLE:
      return withObject('deliverable', 'deliverables');
    case NotificationEntityType.PHASE:
      return { projectId, section: 'phase-plan-v2' };
    default:
      return { projectId, section: 'phase-plan-v2' };
  }
};

export const useNotificationInbox = ({ enabled = true, isOpen = false }: UseNotificationInboxOptions = {}) => {
  const queryClient = useQueryClient();

  // The global query client
  const { data: unreadCount = 0 } = useGetNotificationUnreadCount({
    query: {
      enabled,
      staleTime: 0,
      refetchInterval: POLL_INTERVAL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  });

  const { data, isLoading } = useGetNotification({
    query: {
      enabled: enabled && isOpen,
      staleTime: 0,
      refetchOnMount: true,
    },
  });

  const notifications = useMemo(() => data ?? [], [data]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: INBOX_KEY });
    queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
  }, [queryClient]);

  const markReadMutation = usePutNotificationIdRead({
    mutation: {
      onMutate: ({ id }) => {
        queryClient.setQueryData<NotificationDto[]>(INBOX_KEY, prev =>
          prev?.map(item => (item.id === id ? { ...item, read: true } : item)),
        );
        queryClient.setQueryData<number>(UNREAD_KEY, prev =>
          typeof prev === 'number' ? Math.max(0, prev - 1) : prev,
        );
      },
      onSettled: invalidate,
    },
  });

  const markAllReadMutation = usePutNotificationReadAll({
    mutation: {
      onMutate: () => {
        queryClient.setQueryData<NotificationDto[]>(INBOX_KEY, prev =>
          prev?.map(item => ({ ...item, read: true })),
        );
        queryClient.setQueryData<number>(UNREAD_KEY, 0);
      },
      onSettled: invalidate,
    },
  });

  const markAsRead = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      const target = notifications.find(item => item.id === id);
      if (!target || target.read) return;
      markReadMutation.mutate({ id });
    },
    [notifications, markReadMutation],
  );

  const markAllAsRead = useCallback(() => {
    if (unreadCount === 0) return;
    markAllReadMutation.mutate();
  }, [unreadCount, markAllReadMutation]);

  return { notifications, unreadCount, isLoading, markAsRead, markAllAsRead };
};
